import classnames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';

import {Anchor, Button, Heading, Icon, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1} from '@albert-io/models';
import {getClassroomDomainBackgroundClass} from 'client/Classrooms/classroomDomainColor';
import {history} from 'client/history';

import {AutoArchiveChip, isAutoArchivingSoon} from './AutoArchiveChip';
import {InviteMenu} from './InviteMenu';
import {useStoreChangePump} from './useStoreChangePump';

// Banner-only payload (subjects → domain color, school → co-teacher gate). InviteMenu fetches the
// roster/email/license data on first modal open, keeping student emails off every class tab.
export const getClassDetailHeaderQuery = (classroomId: string) =>
  resource('classroom_v1')
    .mandarkEndpoint(['classrooms_v1', classroomId])
    .include('subjects_v2.curriculum_area_v1,school_v5')
    .withMeta('classroom_v1');

// Dark translucent pills per the design; no on-color button variant exists in atomic yet.
const ON_COLOR_BUTTON_CLASSES =
  'border-none! bg-[rgb(0_0_0/0.25)]! content-primary-inverse-static! hover:bg-[rgb(0_0_0/0.35)]!';

interface Props {
  classroomId: string;
}

/**
 * Class banner: class name, archive state, and an Invite entry point. Rendered
 * via DashboardHeader in TeacherDashboardLayout's header slot; class nav lives
 * in the sidebar.
 */
export const ClassDetailHeader = ({classroomId}: Props) => {
  const query = useMemo(() => getClassDetailHeaderQuery(classroomId), [classroomId]);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  // Read the classroom live from the store (not a one-shot snapshot) so the archived banner reflects
  // an archive/unarchive done from the Settings tab; the pump re-renders the header on store 'change'.
  useStoreChangePump();

  useEffect(() => {
    let isStale = false;
    query
      .getResourcePromise()
      .then(() => {
        if (!isStale) {
          setLoadedId(classroomId);
        }
      })
      // Banner is decorative (name + color); an invalid class already redirects to /classes.
      .catch(() => {});
    return () => {
      isStale = true;
    };
  }, [query, classroomId]);

  const classroom = loadedId === classroomId ? (query.getResource() as ClassroomModelV1) : null;
  const isArchived = Boolean(classroom?.isArchived?.());
  const isOnColor = Boolean(classroom);
  const settingsPath = `/classes/${classroomId}/settings/details`;

  return (
    <div className='shrink-0'>
      {/* No banner background until the class loads, so it doesn't flash a grey block then the color. */}
      <header
        className={classnames(
          'flex min-h-[92px] items-center gap-x2 px-x4 py-x6',
          isOnColor && getClassroomDomainBackgroundClass(classroom)
        )}
      >
        <div className='gap-x1 min-w-x0 flex grow flex-col items-start'>
          {!isArchived && classroom && isAutoArchivingSoon(classroom) && (
            <AutoArchiveChip classroom={classroom} />
          )}
          <Heading
            as='h1'
            className={classnames('m-x0 w-full truncate', {
              'content-primary-inverse-static': isOnColor
            })}
            color='inherit'
            size='m'
          >
            {classroom?.getName() ?? ''}
          </Heading>
        </div>
        {classroom && !isArchived && (
          <InviteMenu
            key={classroomId}
            classroom={classroom}
            classroomId={classroomId}
            trigger={(triggerProps) => (
              <Button
                {...triggerProps}
                className={`${ON_COLOR_BUTTON_CLASSES} shrink-0`}
                roundness='pill'
              >
                <Icon className='mr-x1' icon='user-plus' />
                Invite
              </Button>
            )}
          />
        )}
      </header>
      {isArchived && (
        <div className='gap-x2 background-warning-subtle px-x4 py-x2 flex items-center'>
          <Icon className='content-warning' icon='archive' iconStyle='regular' />
          <Text className='content-warning' size='s'>
            This class is archived. You can unarchive in the{' '}
            <Anchor
              as='button'
              inheritColor
              underlined
              onClick={() => history.pushState(null, settingsPath)}
            >
              class settings
            </Anchor>
            .
          </Text>
        </div>
      )}
    </div>
  );
};
